import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { AnchorLinkComponent } from '../../../components/anchor-button/anchor-link.component';
import { LabelsProviderPipe } from '../../../pipes/labels-provider/labels-provider.pipe';
import { GetSensitiveDataActionComponent } from 'src/app/components/get-sensitive-data-input/get-sensitive-data-input.component';
import { Observable } from 'rxjs';
import { ActionGetAgentStepSensitiveDataResponseModel } from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { getFileExtension } from 'src/app/common/dataLayer/dataLayerUtils';

export enum CitationResourceType {
  FILE,
  WEBSITE,
  MESSAGES,
  UNKNOWN,
}

export type CitedSource = {
  id: string;
  link: string;
  fileName?: string;
  name?: string;
  /** @deprecated Use sensitivityLabel instead. Kept for backward compatibility with old data. */
  sensitivityLabelId?: string;
  /** The sensitivity label object containing both ID and name. */
  sensitivityLabel?: { id?: string; name?: string };
  resourceType: CitationResourceType;
  sensitiveData?: {
    buttonText: string;
    isRawValue: boolean;
    showPopupBtn: boolean;
    sensitiveDataMethod: () => Observable<ActionGetAgentStepSensitiveDataResponseModel>;
  };
};

@Component({
  selector: 'app-ai-agent-citation',
  templateUrl: './ai-agent-citation.component.html',
  styleUrls: ['./ai-agent-citation.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AnchorLinkComponent, CommonModule, LabelsProviderPipe, GetSensitiveDataActionComponent],
})
export class AiAgentCitationComponent {
  citation = input<CitedSource>();

  protected readonly CitationResourceType = CitationResourceType;

  protected readonly resourceTypeIcon = computed(() => {
    const citation = this.citation();
    if (!citation) return 'assets/images/common/file.svg';

    switch (citation.resourceType) {
      case CitationResourceType.WEBSITE:
        return 'assets/images/common/globe.svg';
      case CitationResourceType.MESSAGES:
        return 'assets/images/data-lens/messages-square.svg';
      case CitationResourceType.FILE: {
        const ext = getFileExtension(citation.fileName ?? citation.name);
        return ext ? `assets/images/file-types/${ext.toUpperCase()}.svg` : 'assets/images/common/file.svg';
      }
      case CitationResourceType.UNKNOWN:
      default:
        return 'assets/images/common/file.svg';
    }
  });

  protected readonly displayTitle = computed(() => {
    const citation = this.citation();
    return citation?.fileName ?? citation?.name ?? 'unavailable';
  });

  protected onIconError(ev: Event) {
    (ev.target as HTMLImageElement).src = 'assets/images/common/file.svg';
  }

  protected readonly sensitivity = computed(() => {
    const citation = this.citation();
    // Prefer new sensitivityLabel.name, fallback to deprecated sensitivityLabelId for old data
    const sensitivityLabelName = citation?.sensitivityLabel?.name ?? citation?.sensitivityLabelId;
    if (!sensitivityLabelName) return null;
    return { label: sensitivityLabelName, icon: 'assets/images/status-icons/shield.svg' };
  });
}
